import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import BreadcrumbNav from "@/components/base/BreadcrumbNav";
import { articles, categoryInfo } from "@/mocks/articleData";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-600">文章不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const category = categoryInfo[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <Layout>
      <article className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <BreadcrumbNav
              items={[
                { label: "首页", path: "/" },
                { label: category?.name || article.category, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${category.icon} text-xs`}></i>
                  {category.name}
                </Link>
              )}
            </div>
          </header>

          <div
            className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-heading
              [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-heading
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_ul]:mb-4 [&_ul]:pl-6 [&_ul]:list-disc
              [&_li]:mb-2 [&_li]:leading-relaxed
              [&_strong]:text-foreground-900 [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          ></div>

          {relatedArticles.length > 0 && (
            <div className="border-t border-background-200 pt-10">
              <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="p-4 rounded-xl bg-background-100 hover:bg-background-100/80 transition-colors cursor-pointer"
                  >
                    <h4 className="font-medium text-foreground-900 hover:text-primary-600 transition-colors mb-1.5 text-sm">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2">{related.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            url: `https://aiqingdao.com/article/${article.slug}`,
            author: {
              "@type": "Organization",
              name: "爱情岛网址导航",
            },
            publisher: {
              "@type": "Organization",
              name: "爱情岛网址导航",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://aiqingdao.com" },
                { "@type": "ListItem", position: 2, name: category?.name || article.category, item: `https://aiqingdao.com/category/${article.category}` },
                { "@type": "ListItem", position: 3, name: article.title },
              ],
            },
          }),
        }}
      />
    </Layout>
  );
}